import React from 'react';
import { TextInput } from './inputs/TextInput';
import { MultiSelectInput } from './inputs/MultiSelectInput';
import { DoubleTextInput } from './inputs/DoubleTextInput';
import { preQuestions } from '../../data/preQuestions';

/**
 * Input 컴포넌트 타입별 Factory
 * 새로운 Input 타입 추가 시 여기에 등록
 */
const INPUT_COMPONENTS = {
  single: {
    Text: TextInput,
    MultiSelect: MultiSelectInput,
  },
  group: {
    number: DoubleTextInput,
  },
};

/**
 * Input 컴포넌트를 생성하는 Factory 함수
 * @param {Object} config - Input 설정 객체
 * @param {Object} handlers - 공통 핸들러들
 * @returns {React.Component} 해당하는 Input 컴포넌트
 */
export const createInputComponent = (config, handlers) => {
  const InputComponent = 
    config.type === 'group' ?
    INPUT_COMPONENTS[config.type][config.fields[config.order].inputType] :
    INPUT_COMPONENTS[config.type][config.fields[0].inputType];
  
  if (!InputComponent) {
    console.warn(`Unknown input type: ${config.type}`);
    return null;
  }

  return React.createElement(InputComponent, {
    ...config,
    ...handlers,
  });
};

/**
 * Input 타입별 검증 로직
 */
export const validateInput = (inputType, value, config) => {
  switch (inputType) {
    case 'Text':
      return validateTextInput(value, config.validation);
    case 'MultiSelect':
      return validateMultiSelectInput(value, config);
    case 'DoubleText':
      return validateDoubleTextInput(value, config.validation);
    default:
      return { isValid: false, message: 'Unknown input type' };
  }
};

const validateTextInput = (value, validation) => {
  const numValue = Number(value);
  const min = validation?.min || 1;
  const max = validation?.max || 999;
  
  if (!value || value.length === 0) {
    return { isValid: false, message: '값을 입력해주세요' };
  }
  
  if (isNaN(numValue) || numValue < min || numValue > max) {
    return { 
      isValid: false, 
      message: `${min}~${max}까지 입력 가능합니다.` 
    };
  }
  
  return { isValid: true };
};

const validateMultiSelectInput = (value, config) => {
  if (!value || value.length === 0) {
    return { isValid: false, message: '최소 하나 이상 선택해주세요' };
  }
  
  return { isValid: true };
};

const validateDoubleTextInput = (value, validation) => {
  // DoubleTextInput의 검증 로직 구현
  // 예: 두 개의 값이 모두 유효한지 확인
  if (!value || !value.first || !value.second) {
    return { isValid: false, message: '모든 필드를 입력해주세요' };
  }
  
  return { isValid: true };
};

/**
 * Input 설정을 정규화하는 함수
 * @param {Object} rawConfig - 원본 설정 데이터
 * @returns {Object} 정규화된 설정 객체
 */
export const normalizeInputConfig = (rawConfig, partnerType) => {
  const initConfig = preQuestions[partnerType].questions[rawConfig.order];
  return {
    type: rawConfig.type,
    label: rawConfig.label || initConfig?.label,
    question: rawConfig.question || initConfig?.question,
    placeholder: rawConfig.placeholder || initConfig?.placeholder,
    validation: rawConfig.validation || initConfig?.validation,
    warning: rawConfig.warning || initConfig?.warning,
    options: rawConfig.options || initConfig?.options,
    // DoubleTextInput용 추가 필드들
    fields: rawConfig.fields,
  };
};
